// Libs
import { useEffect, useRef, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { format, parse } from 'date-fns';
import FlatPickr, { DateTimePickerHandle } from 'react-flatpickr';

// RDS
import Heading from '@ramsey-design-system/heading';
import Text from '@ramsey-design-system/text';
import {
  InformationOutlinedIcon,
  SuccessIcon
} from '@ramsey-design-system/icons';

// Components
import WorkshopStepNavigation from '../WorkshopStepNavigation';
import RestrictedDateDialog from '../RestrictedDateDialog';

// Utils
import { analyticsLogger } from '~fpu/shared/utils/analytics';
import { getWorkshopModules } from '../../WorkshopModules/content';
import {
  DEFAULT_START_TIME,
  isRestrictedWorkshopDate,
  TIME_PARSE_REFERENCE
} from '../constants';

// Types
import { WorkshopFormValues } from '../types';

// Styles
import 'flatpickr/dist/flatpickr.css';
import Icon from '@ramsey-design-system/icon';

const TIME_FORMAT = 'HH:mm:ss';
const DATE_FORMAT = 'yyyy-MM-dd';

function WorkshopSchedule() {
  const { control, setValue } = useFormContext<WorkshopFormValues>();
  const values = useWatch({ control });
  const modules = getWorkshopModules('BUDGETING_BASICS');
  const headingRef = useRef<HTMLDivElement>(null);
  const datePickerRef = useRef<DateTimePickerHandle>(null);
  const [restrictedDate, setRestrictedDate] = useState<string | null>(null);

  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  const handleDateChange = (selected: Date[]) => {
    const date = selected[0];
    if (!date) return;

    if (isRestrictedWorkshopDate(date)) {
      setRestrictedDate(format(date, DATE_FORMAT));
      return;
    }

    setValue('workshopDate', format(date, DATE_FORMAT), {
      shouldDirty: true
    });

    analyticsLogger.trackElementChanged({
      element: 'Create Workshop - Workshop Date',
      label: format(date, 'EEEE')
    });
  };

  const handleRestrictedDateDismiss = () => {
    setRestrictedDate(null);
    datePickerRef.current?.flatpickr?.setDate(values.workshopDate || '', false);
  };

  const handleTimeChange = (selected: Date[]) => {
    const time = selected[0];
    if (!time) return;

    setValue('startTime', format(time, TIME_FORMAT), { shouldDirty: true });

    analyticsLogger.trackElementChanged({
      element: 'Create Workshop - Start Time',
      label: format(time, TIME_FORMAT)
    });
  };

  const datePickerOptions = {
    inline: true,
    altFormat: 'D M j, Y',
    altInput: true,
    dateFormat: 'Y-m-d',
    static: true,
    allowInput: false,
    minDate: 'today'
  };

  const timePickerOptions = {
    altFormat: 'h:i K',
    altInput: true,
    enableTime: true,
    noCalendar: true,
    disableMobile: true,
    dateFormat: TIME_FORMAT,
    minuteIncrement: 30,
    time_24hr: false
  };

  const startTime = values.startTime || DEFAULT_START_TIME;

  const disableNextStep = !values.workshopDate;

  return (
    <section>
      <div
        ref={headingRef}
        tabIndex={-1}
        data-testid="workshop-schedule-heading"
        className="outline-none"
      >
        <Heading level="2" className="text-6 mb-3">
          Workshop Schedule
        </Heading>
      </div>

      <Heading level="3" className="text-3 mb-2">
        Workshop Date
      </Heading>

      <div className="mb-4 text-center">
        <FlatPickr
          ref={datePickerRef}
          data-testid="workshop-date-picker"
          className="hidden"
          value={values.workshopDate || undefined}
          onChange={handleDateChange}
          options={datePickerOptions}
        />
      </div>

      <Heading level="3" className="text-3 mb-2">
        Choose a workshop start time:
      </Heading>

      <div className="mb-3 flex flex-col gap-2 sm:flex-row">
        <div className="flex w-[100px] shrink-0">
          <FlatPickr
            className="border-gray-60 w-full cursor-pointer rounded-sm border p-2"
            data-testid="workshop-time-picker"
            value={parse(startTime, TIME_FORMAT, TIME_PARSE_REFERENCE)}
            onChange={handleTimeChange}
            options={timePickerOptions}
          />
        </div>

        <div className="bg-blue-5 flex flex-1 items-center gap-1 rounded-sm p-2 text-blue-50">
          <Icon icon={InformationOutlinedIcon} color="primary" size="large" />
          <Text className="text-4 text-blue-50">
            All {modules.length} sessions will be scheduled for this date
          </Text>
        </div>
      </div>

      <Heading level="3" className="text-3 mb-2">
        Sessions Included
      </Heading>

      <ul
        className="bg-gray-10 mb-3 rounded-sm p-2"
        data-testid="workshop-sessions-included"
      >
        {modules.map((module) => (
          <li key={module.id} className="mb-1 flex items-center gap-1">
            <Icon icon={SuccessIcon} color="primary" size="large" />
            <Text className="text-3">{module.name}</Text>
          </li>
        ))}
      </ul>

      <WorkshopStepNavigation disableNextStep={disableNextStep} />

      <RestrictedDateDialog
        open={Boolean(restrictedDate)}
        onDismiss={handleRestrictedDateDismiss}
        restrictedDate={restrictedDate || ''}
      />
    </section>
  );
}

export default WorkshopSchedule;
